import { cn } from '@lib/cn';
import type { FreshnessState } from '@/types/index';
import { useFreshness } from '@state/useFreshness';

/**
 * Freshness banner — shows current data freshness state above all dashboards.
 * Per spec §11 refresh + per Reporting_v4_Spec.md §12 (schedule runner respects this).
 *
 * Editorial restyle: paper-2 (fresh), amber-bg (stale), crimson-bg (blocked).
 * Uses the `.freshness-banner` class from index.css.
 */
export function FreshnessBanner() {
  const { data, isLoading, isError } = useFreshness();

  if (isLoading || !data) {
    return (
      <div className="freshness-banner fresh" role="status" aria-live="polite">
        <span className="dot" />
        <span>Loading freshness state…</span>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="freshness-banner blocked" role="status" aria-live="polite">
        <span className="dot" />
        <span>Could not read freshness state</span>
      </div>
    );
  }
  const freshness: FreshnessState = data;

  const tone = freshness.kind === 'fresh' ? 'fresh' : freshness.kind === 'stale' ? 'stale' : 'blocked';

  return (
    <div className={cn('freshness-banner', tone)} role="status" aria-live="polite">
      <span className="dot" />
      {freshness.kind !== 'fresh' && <b>{freshness.kind.replace('_', ' ').toUpperCase()}</b>}
      <span>{describeFreshness(freshness)}</span>
    </div>
  );
}

function describeFreshness(f: FreshnessState): string {
  switch (f.kind) {
    case 'fresh':
      return `Snapshot ${f.snapshotDate}`;
    case 'stale':
      return `Last successful refresh: ${f.lastSuccessfulAt}`;
    case 'failed':
      return `Refresh failed: ${f.reason}. Last good: ${f.lastSuccessfulAt}`;
    case 'rolled_back':
      return `Rolled back to ${f.rolledBackTo}: ${f.reason}`;
  }
}
